import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { mktApi, errMsg } from './marketingApi';
import { APPROVAL_METHODS } from './mktShared';

export default function ApprovalDialog({ open, onOpenChange, order, onSaved }) {
  const [result, setResult] = useState('approved');
  const [method, setMethod] = useState('email');
  const [confirmedAt, setConfirmedAt] = useState('');
  const [contact, setContact] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let evidence_file_id = null;
      if (file) {
        const up = await mktApi.uploadFile(file);
        evidence_file_id = up.file_id;
      }
      const updated = await mktApi.approval(order.id, {
        result, method,
        confirmed_at: confirmedAt ? new Date(confirmedAt).toISOString() : null,
        customer_contact: contact || null, note: note || null, evidence_file_id,
      });
      toast.success(result === 'approved' ? '客先承認を記録しました' : '客先却下を記録しました');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mkt-approval-dialog">
        <DialogHeader><DialogTitle>客先承認の記録 — {order?.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setResult('approved')} data-testid="mkt-approval-result-approved"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${result === 'approved' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}>
              承認
            </button>
            <button type="button" onClick={() => setResult('rejected')} data-testid="mkt-approval-result-rejected"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${result === 'rejected' ? 'border-red-500 bg-red-50 text-red-600' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}>
              却下
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">確認方法</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1" data-testid="mkt-approval-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(APPROVAL_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">確認日</label>
              <Input type="date" value={confirmedAt} onChange={(e) => setConfirmedAt(e.target.value)} className="mt-1" data-testid="mkt-approval-date" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">先方担当者</label>
            <Input value={contact} onChange={(e) => setContact(e.target.value)} className="mt-1" data-testid="mkt-approval-contact" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">備考{result === 'rejected' && '（却下理由）'}</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" data-testid="mkt-approval-note" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">証憑ファイル（任意 / 画像・PDF）</label>
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="mkt-approval-evidence" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mkt-approval-cancel-btn">キャンセル</Button>
            <Button onClick={save} disabled={saving} className={result === 'approved' ? 'bg-teal-700 hover:bg-teal-800' : 'bg-red-600 hover:bg-red-700'} data-testid="mkt-approval-save-btn">
              {saving ? '保存中…' : '記録する'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
